import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { technologies } from '../../data/technologies';
import { buildings } from '../../data/buildings';
import { resources } from '../../data/resources';
import { getScaledTechCost } from '../../data/balance';

/**
 * TechChip - A reusable component for displaying technologies with rich tooltips
 * Shows prerequisites, cost, and what it unlocks on hover
 * 
 * Usage:
 *   <TechChip id="metallurgy" />
 *   <TechChip id="writing" showIcon={false} />
 */
function TechChip({ 
  id, 
  showIcon = true,
  showName = true,
  className = '',
  size = 'normal', // 'small', 'normal', 'large'
}) {
  const [tooltip, setTooltip] = useState(null);

  const tech = technologies[id];
  
  if (!tech) {
    return <span className="tc-unknown">❓{id}</span>;
  }

  const handleMouseEnter = (e) => {
    setTooltip({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e) => {
    if (tooltip) setTooltip({ x: e.clientX, y: e.clientY });
  };

  const handleMouseLeave = () => setTooltip(null);

  const sizeClass = size === 'small' ? 'tc-small' : size === 'large' ? 'tc-large' : '';

  return (
    <>
      <span
        className={`tech-chip ${sizeClass} ${className}`}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {showIcon && <span className="tc-icon">{tech.icon}</span>}
        {showName && <span className="tc-name">{tech.name}</span>}
      </span>

      {tooltip && ReactDOM.createPortal(
        <TechTooltip tech={tech} x={tooltip.x} y={tooltip.y} />,
        document.body
      )}
    </>
  );
}

/**
 * TechTooltip - Rich tooltip showing technology details
 */
function TechTooltip({ tech, x, y }) {
  const tooltipWidth = 300;
  const tooltipHeight = 450; // Conservative estimate
  
  let left = x + 15;
  let top = y - 10;
  
  if (left + tooltipWidth > window.innerWidth - 10) left = x - tooltipWidth - 15;
  if (left < 10) left = 10;
  if (top + tooltipHeight > window.innerHeight - 10) top = window.innerHeight - tooltipHeight - 10;
  if (top < 10) top = 10;

  // Get prerequisite techs
  const prereqTechs = (tech.requires || []).map(id => technologies[id]).filter(Boolean);
  
  // Get required buildings
  const reqBuildings = tech.requiresBuildings || {};
  const hasReqBuildings = Object.keys(reqBuildings).length > 0;

  // Get what this tech unlocks
  const unlockedBuildings = (tech.unlocks || []).map(id => buildings[id]).filter(Boolean);
  
  // Find all buildings unlocked by this tech (checking unlockCondition)
  const allUnlockedBuildings = Object.values(buildings).filter(
    b => b.unlockCondition?.tech === tech.id
  );

  // Cost info (scaled by balance.js)
  const cost = getScaledTechCost(tech.cost || {}, tech.tier);

  return (
    <div className="tech-tooltip" style={{ left, top }}>
      {/* Header */}
      <div className="tt-header">
        <span className="tt-icon">{tech.icon}</span>
        <div className="tt-info">
          <span className="tt-name">{tech.name}</span>
          <span className="tt-tier">Tier {tech.tier}</span>
        </div>
      </div>

      {/* Description */}
      <p className="tt-desc">{tech.description}</p>

      {/* Prerequisites */}
      {prereqTechs.length > 0 && (
        <div className="tt-section tt-prereqs">
          <div className="tt-section-title">🔗 Requires Technologies</div>
          <div className="tt-prereq-list">
            {prereqTechs.map(prereq => (
              <span key={prereq.id} className="tt-prereq-item">
                <span className="tt-prereq-icon">{prereq.icon}</span>
                <span className="tt-prereq-name">{prereq.name}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Required Buildings */}
      {hasReqBuildings && (
        <div className="tt-section tt-req-buildings">
          <div className="tt-section-title">🏠 Requires Buildings</div>
          <div className="tt-building-list">
            {Object.entries(reqBuildings).map(([buildingId, count]) => {
              const building = buildings[buildingId];
              return (
                <span key={buildingId} className="tt-building-item">
                  <span className="tt-building-icon">{building?.icon || '❓'}</span>
                  <span className="tt-building-name">{building?.name || buildingId}</span>
                  <span className="tt-building-count">×{count}</span>
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Cost */}
      <div className="tt-section tt-cost">
        <div className="tt-section-title">💰 Research Cost</div>
        <div className="tt-cost-list">
          {Object.entries(cost).map(([resId, amount]) => {
            const res = resources[resId];
            return (
              <span key={resId} className="tt-cost-item">
                <span className="tt-cost-icon">{res?.icon || '❓'}</span>
                <span className="tt-cost-amount">{amount}</span>
                <span className="tt-cost-name">{res?.name || resId}</span>
              </span>
            );
          })}
        </div>
      </div>

      {/* Unlocks */}
      {allUnlockedBuildings.length > 0 && (
        <div className="tt-section tt-unlocks">
          <div className="tt-section-title">🔓 Unlocks Buildings</div>
          <div className="tt-unlock-list">
            {allUnlockedBuildings.slice(0, 6).map(building => (
              <span key={building.id} className="tt-unlock-item">
                <span className="tt-unlock-icon">{building.icon}</span>
                <span className="tt-unlock-name">{building.name}</span>
              </span>
            ))}
            {allUnlockedBuildings.length > 6 && (
              <span className="tt-unlock-more">+{allUnlockedBuildings.length - 6} more</span>
            )}
          </div>
        </div>
      )}

      {/* No prereqs message */}
      {prereqTechs.length === 0 && !hasReqBuildings && (
        <div className="tt-section tt-available">
          <span className="tt-available-text">✅ Can be researched immediately</span>
        </div>
      )}

      {/* Flavor text */}
      {tech.flavor && (
        <div className="tt-flavor">{tech.flavor}</div>
      )}
    </div>
  );
}

export default TechChip;
